import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormValues, projectTypes } from '../FormSchema';

/**
 * First step of the multi-step form - Project Basics
 */
export function ProjectBasicsStep() {
  const { register, formState: { errors }, setValue, watch } = useFormContext<FormValues>();
  const projectType = watch('project_type');
  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="full_name" className="text-white/90">Full Name <span className="text-red-400">*</span></Label>
        <Input
          id="full_name"
          placeholder="Your name"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50"
          {...register('full_name')}
        />
        {errors.full_name && (
          <p className="text-red-400 text-sm mt-1">{errors.full_name.message}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email" className="text-white/90">Email Address <span className="text-red-400">*</span></Label>
        <Input
          id="email"
          type="email"
          placeholder="your.email@example.com"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50"
          {...register('email')}
        />
        {errors.email && (
          <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="company_name" className="text-white/90">Company Name</Label>
        <Input
          id="company_name"
          placeholder="Your company (optional)"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50"
          {...register('company_name')}
        />
        {errors.company_name && (
          <p className="text-red-400 text-sm mt-1">{errors.company_name.message}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="project_type" className="text-white/90">Project Type <span className="text-red-400">*</span></Label>
        <Select 
          value={projectType} 
          onValueChange={(value) => setValue('project_type', value as any)}
        >
          <SelectTrigger 
            id="project_type"
            className="bg-white/5 border-white/10 text-white"
          >
            <SelectValue placeholder="Select project type" />
          </SelectTrigger>
          <SelectContent>
            {projectTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.project_type && (
          <p className="text-red-400 text-sm mt-1">{errors.project_type.message}</p>
        )}
      </div>
      
      {/* Honeypot field - hidden from users but visible to bots */}
      <div style={{ display: 'none' }} aria-hidden="true">
        <Label htmlFor="honeypot" className="sr-only">Leave this empty</Label>
        <Input
          id="honeypot"
          autoComplete="off"
          tabIndex={-1}
          {...register('honeypot')}
        />
      </div>
      
      <div className="mt-8 pb-2 border-t border-white/10 pt-8">
        <h3 className="text-lg font-medium text-white mb-2">What to expect next</h3>
        <p className="text-white/70">
          After completing this form, we'll analyze your requirements and create a customized proposal for your project.
          This includes timeline estimates, budget considerations, and recommended technology stack.
        </p>
      </div>
    </div>
  );
}
